import React from "react";
import {ISupplementsAttributes} from "../../interfaces";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import ClickableCodesArray from "./ClickableCodesArray";

interface Props {
    attributes: ISupplementsAttributes
}

/**
 * Returns a unordered list of clickable codes (used for children or sibling, i.e. similar codes).
 * */
function SupplementsAttributes({ attributes }: Props) {
    const { language } = useParams();
    const { t } = useTranslation();

    function renderRelevantCodes(codes) {
        const zeType = attributes.ze_type;
        return (zeType === "C" ?
            <ClickableCodesArray
                codesArray={codes}
                codesType={'REL_CODES_SUPPLEMENTS'}
                language={language}
                catalog={'CHOP'}
                id={'relevantChopCodes'}
            /> : renderCodesArray(codes, "LBL_REL_CODES_SUPPLEMENTS", "C", 'relevantCodes')
        )
    }

    function renderClickableCodesArray(codes, catalog, codesType) {
        return <ClickableCodesArray
            codesArray={codes}
            codesType={codesType}
            language={language}
            catalog={catalog}
            id={codesType}
        />
    }

    function transformRelevantCodesTitle(title, zeType) {
        let transformedTitle = title
        if (zeType === 'C') {
            return (transformedTitle += " (CHOP)")
        }
        if (zeType === 'A') {
            return (transformedTitle += " (ATC)")
        } else {
            return transformedTitle
        }
    }

    function renderCodesArray(codes, title, codeType, id) {
        return (
            <div id={id}>
                <h5>{transformRelevantCodesTitle(t(title), codeType)}</h5>
                <ul>
                    {codes.map((c, i) => (
                        <li key={c.code + "_" + i}>{c.code + (c.text ? ": " + c.text : "")}</li>
                    ))}
                </ul>
            </div>
        )
    }

    function tableRow(key, value) {
        return (<tr key={key + "_" + value}>
            <td><b>{t('LBL_' + key.toUpperCase())}</b></td>
            <td>{value}</td>
        </tr>)
    }

    function dosageInfo(attrs) {
        return (
            <>
                <tr>
                    <td><b>{t('LBL_DOSE_MIN')}</b></td>
                    <td>{attrs.dose_min + attrs.dose_unit}</td>
                </tr>
                {attrs.dose_max > 0 &&
                    <tr>
                        <td><b>{t('LBL_DOSE_MAX')}</b></td>
                        <td>{attrs.dose_max + attrs.dose_unit}</td>
                    </tr>}
            </>
        )
    }

    // TODO: Render clickable codes array as soon as linking into codes from different catalogs is properly implemented.
    const {terminal} = attributes
    return (
        // If not terminal, there is only text info which is rendered in parent component.
        terminal &&
        <div className="row vertical-spacer">
            <div className="col-lg-6">
                <div className="table-responsive">
                    <table className="table table-striped table-hover" id={'attributesTable'}>
                        <thead>
                        <tr>
                            <th colSpan={2}>{t('LBL_SUPPLEMENTS_CATALOG')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {(() => {
                            return Object.keys(attributes).map((key) => {
                                let value = attributes[key];
                                switch (key) {
                                    case "age_higher_than":
                                    case "age_lower_than":
                                        return value > 0 ? tableRow(key, value) : null;
                                    case 'dosage':
                                        return value !== 0 ? <React.Fragment key={key}>{dosageInfo(attributes)}</React.Fragment> : null;
                                    case 'za':
                                    case 'va':
                                        return value ? tableRow(key, value) : null;
                                    case "amount":
                                        return tableRow(key, value)
                                }
                            })
                        })()}
                        </tbody>
                    </table>
                </div>
            </div>
            {attributes.relevant_codes && attributes.relevant_codes.length > 0 && attributes.relevant_codes[0].code &&
                renderCodesArray(
                    attributes.relevant_codes,
                    'LBL_REL_CODES_SUPPLEMENTS',
                    attributes.ze_type,
                    "RELEVANT_CODES")}
            {attributes.excluded_drgs && attributes.excluded_drgs.length > 0 &&
                renderCodesArray(
                    attributes.excluded_drgs,
                    'LBL_EXCLUDED_DRGS',
                    null,
                    'EXCLUDED_DRGS')}
            {attributes.constraint_icds && attributes.constraint_icds.length > 0 &&
                renderCodesArray(
                    attributes.constraint_icds,
                    'LBL_CONSTRAINT_ICDS',
                    null,
                    'CONSTRAINT_ICDS')}
            {attributes.constraint_chops && attributes.constraint_chops.length > 0 &&
                renderCodesArray(
                    attributes.constraint_chops,
                    'LBL_CONSTRAINT_CHOPS',
                    null,
                    'CONSTRAINT_CHOPS')}
        </div>
    )
}

export default SupplementsAttributes;
